import { ChartTypeRegistry } from 'chart.js';

import { REPORT_TEMPLATES_COLUMNS } from 'interfaces/report-templates.interface';
import { OptionType, TableColumn } from 'interfaces/shared.interface';

import { BASE_FILTER_FIELDS } from './common';

export const REPORT_TEMPLATES_COLUMNS_TITLES = {
  [REPORT_TEMPLATES_COLUMNS.NAME]: 'Name',
  [REPORT_TEMPLATES_COLUMNS.TYPE]: 'Type',
  [REPORT_TEMPLATES_COLUMNS.CREATOR]: 'Creator',
  [REPORT_TEMPLATES_COLUMNS.CREATION_TIME]: 'CreationTime',
  [REPORT_TEMPLATES_COLUMNS.ACTIONS]: '',
};

export const REPORT_TEMPLATES_TABLE_COLUMNS: TableColumn[] = [
  {
    columnId: REPORT_TEMPLATES_COLUMNS.NAME,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.NAME],
  },
  {
    columnId: REPORT_TEMPLATES_COLUMNS.TYPE,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.TYPE],
  },
  {
    columnId: REPORT_TEMPLATES_COLUMNS.CREATOR,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.CREATOR],
  },
  {
    columnId: REPORT_TEMPLATES_COLUMNS.CREATION_TIME,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.CREATION_TIME],
    sorting: true,
  },
  {
    columnId: REPORT_TEMPLATES_COLUMNS.ACTIONS,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.ACTIONS],
  },
];

export const TEMPLATES_DEFAULT_FILTERS_VALUES = {
  [BASE_FILTER_FIELDS.SEARCH_FIELD]: '',
};

export type ReportTypeOption = {
  label: string;
  value: number;
};

export const REPORT_TYPES = [
  { value: 1, label: 'Excel' },
  { value: 2, label: 'CSV' },
];

export enum Condition {
  Is = 1,
  IsNot = 2,
  Contains = 3,
  DoesNotContain = 4,
  StartsWith = 5,
  DoesNotStartWith = 6,
  EndsWith = 7,
  DoesNotEndWith = 8,
  IsNull = 9,
  IsNotNull = 10,
  GreaterThan = 11,
  LessThan = 12,
  GreaterThanOrEqualTo = 13,
  LessThanOrEqualsTo = 14,
}

export enum ReportFilterSectionId {
  JOIN = 'join',
  GROUPING = 'grouping',
  AGGREGATE = 'aggregate',
  FILTER = 'filter',
  SORT = 'sort',
}

export const REPORT_FILTER_SECTIONS = [
  { id: ReportFilterSectionId.JOIN, label: 'AddJoin' },
  { id: ReportFilterSectionId.GROUPING, label: 'Grouping' },
  { id: ReportFilterSectionId.AGGREGATE, label: 'AggregateFunctions' },
  { id: ReportFilterSectionId.FILTER, label: 'Filters' },
  { id: ReportFilterSectionId.SORT, label: 'SortBy' },
];

export type AggregateFunctionType =
  | 'Count'
  | 'Distinct count of'
  | 'Sum of'
  | 'Average of'
  | 'Maximum of'
  | 'Minimum of';

export const AGGREGATE_OPTIONS: AggregateFunctionType[] = [
  'Count',
  'Distinct count of',
  'Sum of',
  'Average of',
  'Maximum of',
  'Minimum of',
];

export const AGGREGATE_FUNCTION_TYPE_MAP: Record<AggregateFunctionType, number> = {
  Count: 1,
  'Distinct count of': 2,
  'Sum of': 3,
  'Average of': 4,
  'Maximum of': 5,
  'Minimum of': 6,
};

export const AGGREGATE_FUNCTION_NUMBER_MAP: Record<number, AggregateFunctionType> = {
  1: 'Count',
  2: 'Distinct count of',
  3: 'Sum of',
  4: 'Average of',
  5: 'Maximum of',
  6: 'Minimum of',
};

export const getAggregateFunctionTypeString = (numOrString: number | string | null): AggregateFunctionType => {
  if (typeof numOrString === 'string') {
    return numOrString as AggregateFunctionType;
  }
  if (typeof numOrString === 'number') {
    return AGGREGATE_FUNCTION_NUMBER_MAP[numOrString] || 'Sum of';
  }
  return 'Sum of';
};

export const getAggregateFunctionNumber = (func: unknown): number => {
  if (typeof func === 'string' && func in AGGREGATE_FUNCTION_TYPE_MAP) {
    return AGGREGATE_FUNCTION_TYPE_MAP[func as AggregateFunctionType];
  }
  return 3;
};

export type AggregateItem = {
  id: number;
  func: AggregateFunctionType | null;
  column?: string | null;
};

export type FilterItem = {
  id: string;
  column: string | null;
  condition: number | null;
  value: any;
  locked: boolean;
  operator?: 'And' | 'Or';
};

export const FILTER_CONDITIONS_FOR_NEW_FILTER = [
  { label: 'And', value: 'And' },
  { label: 'Or', value: 'Or' },
];

export const FILTER_CONDITIONS_FOR_NULL_CONDITIONS = [
  { id: Condition.IsNull, label: 'Is null' },
  { id: Condition.IsNotNull, label: 'Is not null' },
];

export const FILTER_CONDITIONS_FOR_NUMBERS = [
  { id: Condition.Is, label: 'Is' },
  { id: Condition.IsNot, label: 'Is not' },
  { id: Condition.GreaterThan, label: 'Greater than' },
  { id: Condition.LessThan, label: 'Less than' },
  { id: Condition.GreaterThanOrEqualTo, label: 'Greater than or equal to' },
  { id: Condition.LessThanOrEqualsTo, label: 'Less than or equal to' },
  ...FILTER_CONDITIONS_FOR_NULL_CONDITIONS,
];

export const FILTER_CONDITIONS_FOR_STRINGS = [
  { id: Condition.Is, label: 'Is' },
  { id: Condition.IsNot, label: 'Is not' },
  { id: Condition.Contains, label: 'Contains' },
  { id: Condition.DoesNotContain, label: 'Does not contain' },
  { id: Condition.StartsWith, label: 'Starts with' },
  { id: Condition.DoesNotStartWith, label: 'Does not start with' },
  { id: Condition.EndsWith, label: 'Ends with' },
  { id: Condition.DoesNotEndWith, label: 'Does not end with' },
  ...FILTER_CONDITIONS_FOR_NULL_CONDITIONS,
];

export const FILTER_CONDITIONS_FOR_BOOLEANS = [
  { id: Condition.Is, label: 'Is' },
  { id: Condition.IsNot, label: 'Is not' },
  ...FILTER_CONDITIONS_FOR_NULL_CONDITIONS,
];

export enum FilterOperator {
  And = 1,
  Or = 2,
}

export const getOperatorString = (op?: string | null): `And` | 'Or' | undefined => {
  if (op === undefined || op === null) {
    return undefined;
  }

  const num = typeof op === 'string' && op.trim() !== '' && !Number.isNaN(Number(op)) ? Number(op) : op;
  if (num === FilterOperator.And) {
    return 'And';
  }
  if (num === FilterOperator.Or) {
    return 'Or';
  }

  return undefined;
};

export const ASCENDING_DIRECTION = '1';
export const DESCENDING_DIRECTION = '2';

export type SortDirection = typeof ASCENDING_DIRECTION | typeof DESCENDING_DIRECTION;

export const SORT_DIRECTIONS = [
  { label: 'Ascending', value: ASCENDING_DIRECTION },
  { label: 'Descending', value: DESCENDING_DIRECTION },
];

export const normalizeSortDirection = (raw: unknown): SortDirection => {
  if (raw === 'asc' || raw === 1 || raw === ASCENDING_DIRECTION) {
    return ASCENDING_DIRECTION;
  }
  if (raw === 'desc' || raw === 2 || raw === DESCENDING_DIRECTION) {
    return DESCENDING_DIRECTION;
  }
  return ASCENDING_DIRECTION;
};

export type VisualizationType = 'Table' | 'Bar' | 'Line' | 'Pie' | 'Doughnut' | 'Scatter' | 'Polar Area' | 'Radar';

export const VISUALIZATION_OPTIONS = [
  { label: 'Table', value: 'Table' },
  { label: 'Bar', value: 'Bar' },
  { label: 'Line', value: 'Line' },
  { label: 'Pie', value: 'Pie' },
  { label: 'Doughnut', value: 'Doughnut' },
  { label: 'Scatter', value: 'Scatter' },
  { label: 'Polar Area', value: 'Polar Area' },
  { label: 'Radar', value: 'Radar' },
];

export type VisualizationFormState =
  | {
      type: 'Table';
      columns: string[];
      rowLimit?: number;
    }
  | {
      type: Exclude<VisualizationType, 'Table'>;
      column: string;
      timelapseType?: number;
    };

export const getTypeFromDataType = (dataType: string): number => {
  const map: Record<string, number> = {
    INT: 3,
    VARCHAR: 2,
    BOOL: 4,
    DATE: 5,
    DATETIME: 5,
  };
  return map[dataType.toUpperCase()];
};

export const getDisplayTypeFromDataType = (dataType?: string | null): string => {
  const map: Record<string, string> = {
    INT: 'INT',
    VARCHAR: 'VARCHAR',
    BOOL: 'BOOLEAN',
    DATE: 'DATE',
    DATETIME: 'DATETIME',
  };
  if (!dataType) {
    return '';
  }
  const key = String(dataType).toUpperCase();
  return map[key] ?? key;
};

export const getTimelapseTypeFromDataType = (dataType: string): number => {
  const map: Record<string, number> = {
    DATE: 1,
    DATETIME: 1,
  };
  return map[dataType.toUpperCase()];
};

export const chartTypeMap: Record<string, keyof ChartTypeRegistry> = {
  Bar: 'bar',
  Line: 'line',
  Pie: 'pie',
  Doughnut: 'doughnut',
  Scatter: 'scatter',
  'Polar Area': 'polarArea',
  Radar: 'radar',
};

export const FILTER_TIMELAPSE_TIME: OptionType[] = [
  { value: 1, label: 'Yearly' },
  { value: 2, label: 'Monthly' },
  { value: 3, label: 'Weekly' },
  { value: 4, label: 'Daily' },
];

export const typeStringMap: Record<number, string> = {
  1: 'VARCHAR',
  2: 'VARCHAR',
  3: 'INT',
  4: 'BOOL',
  5: 'DATETIME',
};

export type TableRow = Record<string, string | number | Date | null>;

export const visualizationTypeMap: Record<VisualizationType, number> = {
  Table: 1,
  Bar: 2,
  Line: 3,
  Pie: 4,
  Doughnut: 5,
  Scatter: 6,
  'Polar Area': 7,
  Radar: 8,
};

export const visualizationTypeByIdMap: Record<number, VisualizationType> = Object.fromEntries(
  Object.entries(visualizationTypeMap).map(([k, v]) => [v, k as VisualizationType])
) as Record<number, VisualizationType>;

export const parseQualifiedColumn = (value: string): { table: string | undefined; column: string } => {
  if (!value?.includes('.')) {
    return { table: undefined, column: value };
  }
  const dotIndex = value.indexOf('.');
  return { table: value.substring(0, dotIndex), column: value.substring(dotIndex + 1) };
};

export const qualifyColumn = (tableName: string, columnName: string): string => `${tableName}.${columnName}`;

export const extractColumnName = (val: string): string => {
  const { column } = parseQualifiedColumn(val);
  return column;
};

export const matchesGrouping = (optValue: string | number, groupVal: string | number): boolean => {
  const ov = String(optValue);
  const gv = String(groupVal);
  if (ov === gv) {
    return true;
  }
  if (extractColumnName(ov) === gv) {
    return true;
  }
  return false;
};

export const normalize = (v: string): string => {
  return v.replace(/\.[\d.]*$/, '');
};
